/**
 * Every TanStack Query key used by the application.
 *
 * Keys live in one module so that a mutation can invalidate precisely what it changed without importing the
 * feature that owns the query — the single most common source of stale UI in a query-based application.
 */

import type { WizardOptionSort } from '@/types/api';

/** Query keys, grouped by the resource they describe. */
export const queryKeys = {
  /** Profile, roles and entitlements of the signed-in account. */
  profile: ['users', 'me'] as const,

  /** Active device sessions of the signed-in account. */
  sessions: ['auth', 'sessions'] as const,

  /**
   * The registration and verification policy, as the administration area reads it (TASK-0062).
   *
   * Separate from `authPolicy` (the public slice) because this one carries the source, the version the conditional write echoes and
   * the configured fallback.
   */
  adminAuthPolicy: ['admin', 'auth-policy'] as const,

  /**
   * The enabled sign-in methods, as the administration area reads and writes them (TASK-0063).
   *
   * A key of its own rather than a suffix of `adminAuthPolicy`: the settings card is a document with its own version and its own
   * conditional write, and invalidating one must not force the other to re-read.
   */
  adminSignInMethods: ['admin', 'auth-policy', 'sign-in-methods'] as const,

  /**
   * The registration and verification policy in force (TASK-0062).
   *
   * One shared entry behind a five-minute `staleTime`, invalidated when an administrator changes the policy: the verification and
   * registration screens must agree about the channel, and re-reading it on every keystroke would be pointless traffic.
   */
  authPolicy: ['auth', 'policy'] as const,

  /**
   * The public OIDC client configuration of the organisation sign-in (TASK-0064).
   *
   * A key of its own rather than a member of `authPolicy`: the answer carries the deployment's registered callback and scopes,
   * and both the sign-in button and the callback page must build their OIDC client from the same cached read. The API answers
   * `404` for a disabled or unconfigured provider, so a failed read is a state, not a retry loop.
   */
  organisationClient: ['auth', 'organisation-client'] as const,

  /**
   * The provider identities attached to the signed-in account (TASK-0062).
   *
   * Under `external-identities` rather than `auth` because it is a property of the account, invalidated by a link or an unlink.
   */
  externalIdentities: ['users', 'me', 'external-identities'] as const,

  /**
   * Forge balance, lifetime usage and the most recent movements of the signed-in account (TASK-0035 §5.7).
   *
   * One key rather than three: `GET /users/me/forge` answers all of it in a single request, so splitting the key would
   * only produce three cache entries that a single mutation has to invalidate together.
   */
  forgeBalance: ['users', 'me', 'forge'] as const,

  /** One page of the Forge ledger; the page offset is part of the key because it changes the answer. */
  forgeLedger: (skip: number, take: number) =>
    ['users', 'me', 'forge', 'ledger', { skip, take }] as const,

  /** The effective Forge price table; administrator-only on the API side (TASK-0035 §5.8). */
  forgePricing: ['admin', 'forge', 'pricing'] as const,

  /** Every provider profile, as the administrator panel reads it (ADR-0019); the flag changes the answer. */
  providerProfiles: (includeDeleted: boolean) =>
    ['admin', 'provider-profiles', { includeDeleted }] as const,

  /** The prompt-pattern catalog, as the administrator panel reads it (TASK-0039); the flag changes the answer. */
  promptPatterns: (includeDeleted: boolean) =>
    ['admin', 'prompt-patterns', { includeDeleted }] as const,

  /**
   * One page of the public-identifier blacklist, as the administration panel reads it (TASK-0060).
   *
   * The page participates in the key because it changes the answer. A mutation invalidates the `['admin',
   * 'public-identifiers']` prefix rather than this one entry: adding an entry can change what the *next* page holds, and
   * releasing one removes a row from a page that is already cached.
   */
  adminPublicIdentifierBlacklist: (page: number, pageSize: number) =>
    ['admin', 'public-identifiers', 'blacklist', { page, pageSize }] as const,

  /**
   * One account, as the administration surface reads it by its public handle (TASK-0060).
   *
   * Keyed by the normalized handle the administrator typed: two casings of the same handle are the same lookup, and the
   * server answers them identically.
   */
  adminUserByPublicIdentifier: (publicIdentifier: string) =>
    ['admin', 'users', 'by-public-identifier', publicIdentifier] as const,

  /**
   * One page of the administration account list (TASK-0061).
   *
   * The filter and the page participate in the key because both change the answer: two filters genuinely produce two
   * different pages, and the server computed the total each page was read with. A mutation invalidates the whole
   * `['admin', 'users']` prefix rather than this entry, because an edit can also move an account out of the filter it was
   * listed under.
   */
  adminUsers: (filter: unknown, page: number, pageSize: number) =>
    ['admin', 'users', 'list', { filter, page, pageSize }] as const,

  /** One account in full, as the administration detail page reads it (TASK-0061). */
  adminUser: (userId: string) => ['admin', 'users', 'detail', userId] as const,

  /** The read-only credit report of one account (TASK-0061); no mutation refreshes it. */
  adminUserCredit: (userId: string) => ['admin', 'users', 'detail', userId, 'credit'] as const,

  /**
   * The category rail, as the administration panel reads it (TASK-0052); the flag changes the answer.
   *
   * Both taxonomy keys start with `['admin', 'categories']`, which is the prefix every taxonomy mutation invalidates: a
   * rename of a category also moves the child count a list row shows, so the two entries are never stale independently.
   */
  adminCategories: (includeDeleted: boolean) =>
    ['admin', 'categories', 'list', { includeDeleted }] as const,

  /**
   * The subcategories beneath one parent (TASK-0052).
   *
   * The parent participates in the key because no subcategory list exists without it: two categories hold disjoint
   * children, and a request that forgot the parent would be a request for an answer that has no meaning.
   */
  adminSubCategories: (categoryId: string, includeDeleted: boolean) =>
    ['admin', 'categories', 'subcategories', categoryId, { includeDeleted }] as const,

  /**
   * The picker's view of the rail (TASK-0053): live, enabled rows only, readable without the administrator role.
   *
   * A separate entry from {@link queryKeys.adminCategories} on purpose — the two answer different questions (what may be
   * chosen vs. what exists) and carry different flags, so sharing one entry would serve a stale child count or a row the
   * picker must not offer. A taxonomy mutation invalidates this prefix explicitly, beside the administration one, because
   * a rename or a deletion changes both answers.
   */
  taxonomyCategories: () => ['taxonomy', 'categories'] as const,

  /** The live subcategories beneath one parent, as a picker reads them (TASK-0053). */
  taxonomySubCategories: (categoryId: string) => ['taxonomy', 'subcategories', categoryId] as const,

  /**
   * One page of the category rail, as the async picker reads it (TASK-0056).
   *
   * The search text and the page participate in the key, which is what makes a page cached and a search answered from the
   * cache: the same text scrolled to the same position asks the server once, while a new text is a new entry rather than a
   * row set filtered locally.
   */
  taxonomyCategoryPage: (search: string, page: number) =>
    ['taxonomy', 'categories', 'page', { search, page }] as const,

  /**
   * The prompt patterns a user may select for a level, with the policy's suggestion flag (TASK-0039 §6).
   *
   * The level participates in the key because it changes the answer: a pattern authored for `Expert` is not offered at
   * `Simple`. The list changes when an administrator edits the catalog, so it is cached like other catalogue reads.
   */
  selectablePromptPatterns: (level: string) => ['wizard', 'prompt-patterns', level] as const,

  /** The platform profiles a user may select; filtered server-side to the selectable ones (ADR-0019 §D3). */
  selectableProviderProfiles: ['ai', 'provider-profiles'] as const,

  /** Provider and model catalog used by the AI settings and the wizard co-pilot. */
  aiProviders: ['ai', 'providers'] as const,

  /**
   * Target-model registry: what a produced prompt may be written for, and what a new one starts with (TASK-0010 §6).
   *
   * Static product content rather than account data — the registry changes when the product ships a model, not when the
   * user does anything — so it is cached for a day like the wizard's step catalogue.
   */
  targetModels: ['targets', 'models'] as const,

  /**
   * Step catalog of the wizard: steps, option tokens and custom-input policy.
   *
   * Static product content rather than account data, so it is cached for a day and shared by every draft.
   */
  wizardSteps: ['wizard', 'steps'] as const,

  /**
   * The wizard of one `(target model, prompt level)` pair (TASK-0012 §2.2, §3).
   *
   * Both halves of the pair participate in the key, because both change the answer: another level resolves another
   * framework file and therefore another step list, and another model may resolve another family's file. A `null` model
   * is the wizard's opening state — the product's own steps — and is a key of its own rather than "no query yet".
   */
  frameworkSteps: (level: string, targetModelId: string | null) =>
    ['wizard', 'framework', level, { targetModelId }] as const,

  /** One wizard draft with all of its answers (`["wizard","session",id]`, ADR-0009 §5). */
  wizardSession: (sessionId: string) => ['wizard', 'session', sessionId] as const,

  /**
   * One step's ranked options (TASK-0002, TASK-0004).
   *
   * The domains, the search and the sort all change the answer, so each of them participates in the key rather than
   * being held outside it; the domains are passed already canonicalised (deduplicated and sorted) by the caller, so
   * selecting the same domains in another order reuses one cache entry. A step whose catalogue says "not curated" never
   * reaches the API at all.
   */
  wizardOptions: (
    stepKey: string,
    domains: readonly string[],
    search: string,
    sort: WizardOptionSort,
  ) => ['wizard', 'options', stepKey, { domains, search, sort }] as const,

  /** Drafts of the signed-in account, filtered by lifecycle state. */
  wizardSessions: (status: string | null) => ['wizard', 'sessions', { status }] as const,

  /**
   * One page of the prompt library.
   *
   * The filter participates in the key rather than being held outside it: the API computes the sidebar facets over the
   * *filtered* set, so two filters genuinely produce two different answers and must not share a cache entry.
   */
  prompts: (filter: unknown) => ['prompts', 'list', filter] as const,

  /** One prompt in full. */
  prompt: (promptId: string) => ['prompts', 'detail', promptId] as const,

  /** Version history of one prompt; only fetched when the history panel is opened. */
  promptVersions: (promptId: string) => ['prompts', 'versions', promptId] as const,

  /** One snapshot of one prompt, body included when it was retained. */
  promptVersion: (promptId: string, version: number) =>
    ['prompts', 'versions', promptId, 'version', version] as const,

  /** Comparison of two snapshots of one prompt. */
  promptVersionDiff: (promptId: string, fromVersion: number, toVersion: number) =>
    ['prompts', 'versions', promptId, 'diff', { fromVersion, toVersion }] as const,

  /** Live share link of one prompt. */
  promptShare: (promptId: string) => ['prompts', 'share', promptId] as const,

  /** One page of the template catalogue, with its domain facets. */
  templates: (filter: unknown) => ['templates', 'list', filter] as const,

  /** One template in full, addressed by identifier or by slug. */
  template: (idOrSlug: string) => ['templates', 'detail', idOrSlug] as const,

  /** Featured templates of the dashboard carousel. */
  featuredTemplates: ['templates', 'featured'] as const,

  /** Version history of one template; admin-only on the API side. */
  templateVersions: (templateId: string) => ['templates', 'versions', templateId] as const,

  /**
   * One Prompt Store publication draft, as its author reads it back (TASK-0043 §3).
   *
   * Keyed by the publication's own identifier rather than by the prompt it snapshots: a prompt may legitimately carry
   * more than one submission over time, and reopening the draft of a rejected submission must not collide with the
   * fresh one opened after it.
   */
  publicationDraft: (publicationId: string) => ['publications', 'draft', publicationId] as const,

  /**
   * The publication packages of one library prompt, as its workspace lists them (TASK-0044).
   *
   * Keyed by the prompt rather than by a page: the workspace asks for the entry that belongs to *this* prompt, and a
   * prompt that carries more than one submission still answers one bounded list.
   */
  promptPublications: (promptId: string) => ['publications', 'list', promptId] as const,

  /**
   * One page of the moderator's pending-review queue (TASK-0049, TASK-0050).
   *
   * The page participates in the key because it changes the answer: the queue is ordered oldest submission first and
   * spans as many pages as there are waiting submissions. A decision does not patch this cache by hand — it invalidates
   * the whole `['admin', 'publications']` prefix, because approving a row can also change what the *next* page holds.
   */
  adminPendingPublications: (page: number, pageSize: number) =>
    ['admin', 'publications', 'pending-review', { page, pageSize }] as const,

  /**
   * The public store's discovery answer: the models and categories published entries actually carry (TASK-0066).
   *
   * One entry for the whole store shell. The two mega-menus read it, and the store homepage (TASK-0067) reads the same
   * answer for its own rails, so navigating between them costs no request. It is a store-wide prefix on purpose: an
   * action that publishes or withdraws an entry can invalidate `['store']` without knowing which screen is mounted.
   */
  storeHomepage: ['store', 'homepage'] as const,

  /**
   * One page of the public store catalogue, as the homepage's "Explore all" grid pages it (TASK-0067).
   *
   * Keyed by the *filter* rather than by the page number: an infinite query keeps its pages inside its own cache entry,
   * and the page number is what its `pageParam` carries. The filter is a value object with no members today, so the
   * whole catalogue has exactly one entry — and when the listing (TASK-0068) starts filtering, two filters genuinely
   * produce two answers and must not share a cache slot.
   *
   * It sits under the `store` prefix so an action that publishes or withdraws an entry invalidates the rails, the
   * navigation and the grid with one call.
   */
  storeCatalogue: (filter: unknown) => ['store', 'catalogue', filter] as const,

  /**
   * One page of the public store catalogue as the listing's browse screen reads it (TASK-0068).
   *
   * A plain page query, not an infinite one: the address names the page, so each page is its own answer and its own cache
   * entry. The key is the API's query object — filter, order and page — because two of those genuinely produce two answers
   * and must never share a slot; the page size is part of it for the same reason.
   *
   * It sits under the `store` prefix too, so publishing or withdrawing an entry invalidates the rails, the navigation, the
   * homepage grid and every cached listing page with one call.
   */
  storeListing: (filter: unknown) => ['store', 'listing', filter] as const,

  /**
   * One published store entry, as its page reads it (TASK-0069).
   *
   * Keyed by the slug the entry is addressed by, because the slug *is* the identity a visitor followed; the API answers
   * the same `404` for an entry that does not exist and for one that is not published, so a failed read must not be
   * mistaken for a cache miss and retried into an answer that cannot come.
   *
   * It sits under the `store` prefix, like every other store read.
   */
  storeEntry: (slug: string) => ['store', 'entry', slug] as const,

  /**
   * One author's public profile, as its page reads it (TASK-0070).
   *
   * Keyed by the normalized handle and the requested page: the server resolves a handle case-insensitively, so two
   * spellings of one handle are one answer and must share one cache entry rather than fetching the same profile twice.
   * The page is part of the key because each page of the author's entries is its own answer.
   *
   * It sits under the `store` prefix, like every other store read, so an action that publishes or withdraws an entry
   * invalidates a profile as it invalidates the catalogue.
   */
  storeAuthorProfile: (username: string, page: unknown) =>
    ['store', 'author', username.trim().toLowerCase(), page] as const,

  /**
   * The caller's store cart, priced from the live catalogue (TASK-0072).
   *
   * Under a `store-purchases` prefix rather than under `store`, because it is account data: a store action that publishes or
   * withdraws an entry must not have to know that somebody's cart exists, and a cart change must not invalidate the catalogue.
   */
  storeCart: ['store-purchases', 'cart'] as const,

  /** The caller's Forge Credit and its most recent movements (TASK-0072). */
  storeWallet: ['store-purchases', 'wallet'] as const,

  /** The store publications the caller owns (TASK-0072). */
  storeOwnedPublications: ['store-purchases', 'publications'] as const,

  /** One of the caller's own orders, which is the receipt a checkout lands on (TASK-0072). */
  storeOrder: (orderId: string) => ['store-purchases', 'order', orderId] as const,

  /** One page of the caller's Forge Credit movements (TASK-0073). */
  storeWalletLedger: (skip: number, take: number) =>
    ['store-purchases', 'wallet-ledger', skip, take] as const,

  /** One page of the caller's order history (TASK-0073). */
  storeOrderHistory: (page: number, pageSize: number) =>
    ['store-purchases', 'orders', page, pageSize] as const,

  /**
   * The discount codes assigned to the caller (TASK-0073).
   *
   * Under the `store-purchases` prefix like every other account read, so a checkout can invalidate it: a code the buyer just
   * spent has one fewer redemption left.
   */
  myDiscountCodes: ['store-purchases', 'discounts'] as const,

  /**
   * One page of the administrative code list (TASK-0073).
   *
   * Under its own `admin-discounts` prefix, because a code change invalidates the list and the report but must not touch the
   * store catalogue, and a store action must not invalidate the panel.
   */
  adminDiscounts: (filter: unknown) => ['admin-discounts', 'codes', filter] as const,

  /** One page of the redemption report (TASK-0073). */
  adminDiscountRedemptions: (filter: unknown) =>
    ['admin-discounts', 'redemptions', filter] as const,
} as const;
